<template>
  <div>
    <v-card class="pa-4 group-publications-card">
      <v-card-title class="d-flex align-center pb-2 v-card-title">
        <v-icon left class="mr-2">fa-solid fa-layer-group</v-icon>
        {{ $t('components.views.settings.posting_rules.group_publications.group_publications') }}
      </v-card-title>
      <v-card-text>
        <v-list class="pa-0">
          <v-list-item v-for="(item, index) in publications" :key="index" class="border-bottom">
            <v-list-item-icon>
              <v-icon>fa-solid fa-layer-group</v-icon>
            </v-list-item-icon>
            <v-list-item-content @click="onEditPublication(item)" style="cursor: pointer;">
              <v-list-item-title>
                {{ renderTitle(item) }}
              </v-list-item-title>
              <v-list-item-subtitle class="text--secondary">
                {{ renderSubtitle(item) }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon color="error" @click.stop="onDeletePublication(item)"
                :title="$t('components.views.settings.posting_rules.group_publications.delete_group')">
                <v-icon>fa-solid fa-trash</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn color="#1D1B59" dark class="rounded-lg" @click="onNewPublication()">
          <v-icon left>fa-solid fa-plus</v-icon>
          {{ $t('components.views.settings.posting_rules.group_publications.create_group') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="dialogNew" max-width="1000px" persistent>
      <ModalFormGroupPublication :key="componentKey" :edit="editMode" :data="selectedPublication"
        @closeForm="onCloseForm" @reloadTable="onReloadPublications" />
    </v-dialog>

    <v-dialog v-model="dialogDelete" max-width="400px" persistent>
      <v-card class="text-center pa-4">
        <v-card-text>
          <h3 class="mb-3">{{ $t('components.views.settings.posting_rules.group_publications.confirm_delete') }}</h3>
          <p class="mb-0">{{ $t('components.views.settings.posting_rules.group_publications.confirm_delete_message') }}
          </p>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn color="grey" text class="rounded-lg mr-2" @click="dialogDelete = false">
            {{ $t('components.views.settings.posting_rules.group_publications.cancel') }}
          </v-btn>
          <v-btn color="red darken-2" dark class="rounded-lg" @click="confirmDelete">
            {{ $t('components.views.settings.posting_rules.group_publications.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
export default {
  components: {
    ModalFormGroupPublication: () =>
      import(
        /* webpackChunkName: "ModalFormGroupPublication" */ '@/components/views/Settings/PostingRules/GroupPublications/Modals/ModalFormGroupPublication/ModalFormGroupPublication.vue'
      ),
  },
  data() {
    return {
      componentKey: 0,
      dialogNew: false,
      dialogDelete: false,
      publications: [],
      editMode: false,
      selectedPublication: null,
      publicationToDelete: null,
    };
  },
  name: 'GroupPublications',
  computed: {
    ...mapGetters({
      user: 'auth/user',
    }),
    idCompany() {
      const userMap = JSON.parse(this.user);
      return userMap.user.idCompany;
    },
  },
  metaInfo() {
    return {
      title: this.$t('components.views.settings.posting_rules.group_publications.group_publications'),
    };
  },
  mounted() {
    this.loadPublications();
  },
  methods: {
    ...mapActions('snackbar', ['showSnack']),
    ...mapActions('loader', ['showLoader']),

    async loadPublications() {
      this.showLoader({ show: true });
      try {
        const response = await this.axios.get('/PublicationsGroups/idCompany');
        this.publications = response.data || [];
      } catch (error) {
        console.error('Error loading publications:', error);
        this.publications = [];
        this.showSnack({
          text: error.response?.data || 'Error al cargar las publicaciones por grupo',
          title: this.$t('components.views.settings.groups.error'),
          name: 'error',
        });
      } finally {
        this.showLoader({ show: false });
      }
    },

    renderTitle(item) {
      // Mostrar el nombre si existe, sino mostrar los grupos seleccionados
      if (item?.name) {
        return item.name;
      }
      if (item && Array.isArray(item.selectedGroups) && item.selectedGroups.length) {
        return item.selectedGroups.join(', ');
      }
      return this.$t('components.views.settings.posting_rules.group_publications.group_publications');
    },

    renderSubtitle(item) {
      const parts = [];

      // Agregar estado al inicio
      if (item?.status) {
        const statusText = item.status === 'Activo' ? '✓ Activo' : '✗ Inactivo';
        parts.push(statusText);
      }

      // Agregar grupos seleccionados si existe nombre
      if (item?.name && Array.isArray(item.selectedGroups) && item.selectedGroups.length) {
        parts.push(`Grupos: ${item.selectedGroups.join(', ')}`);
      }

      if (item?.country) parts.push(item.country);
      if (item?.originCity || item?.destinationCity) {
        parts.push(`${item.originCity || ''} → ${item.destinationCity || ''}`.trim());
      }

      const opText = this.formatOperationType(item?.operationType);
      if (opText) parts.push(opText);
      if (item?.freight !== null && item?.freight !== undefined) parts.push(`${this.$t('components.views.settings.posting_rules.group_publications.freight')}: ${item.freight}`);
      return parts.filter(Boolean).join(' · ');
    },

    formatOperationType(operationType) {
      if (!operationType) return '';
      const pickDesc = (x) => {
        if (x == null) return '';
        if (typeof x === 'string') return x;
        if (typeof x === 'object') return x.description || x.Description || '';
        return String(x);
      };

      if (Array.isArray(operationType)) {
        const items = operationType.map(pickDesc).filter(Boolean);
        return items.length ? items.join(', ') : '';
      }

      return pickDesc(operationType);
    },

    onNewPublication() {
      this.componentKey += 1;
      this.editMode = false;
      this.selectedPublication = null;
      this.dialogNew = true;
    },

    onEditPublication(item) {
      this.componentKey += 1;
      this.editMode = true;
      this.selectedPublication = item;
      this.dialogNew = true;
    },

    onCloseForm() {
      this.dialogNew = false;
      this.editMode = false;
      this.selectedPublication = null;
    },

    onReloadPublications() {
      this.loadPublications();
    },

    onDeletePublication(item) {
      this.publicationToDelete = item;
      this.dialogDelete = true;
    },

    async confirmDelete() {
      this.showLoader({ show: true });
      try {
        await this.axios.get(`/PublicationsGroups/delete/${this.publicationToDelete.id}`);
        this.showSnack({
          text: this.$t('components.views.settings.posting_rules.group_publications.success_delete'),
          title: this.$t('components.views.settings.groups.success'),
          name: 'success',
        });
        this.dialogDelete = false;
        this.publicationToDelete = null;
        await this.loadPublications();
      } catch (error) {
        console.error('Error deleting publication:', error);
        this.showSnack({
          text: error.response?.data || this.$t('components.views.settings.posting_rules.group_publications.error_delete'),
          title: this.$t('components.views.settings.groups.error'),
          name: 'error',
        });
      } finally {
        this.showLoader({ show: false });
      }
    },
  },
};
</script>

<style scoped>
.rounded-lg {
  border-radius: 8px !important;
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.v-list-item {
  padding: 12px 16px;
}

.v-list-item__title {
  font-size: 16px;
  font-weight: 500;
}

.v-card-title {
  background-color: #f5f5f5;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  padding: 16px 20px;
}

.headline {
  font-size: 18px !important;
  font-weight: 600;
}

.group-publications-card {
  max-height: 400px;
  display: flex;
  flex-direction: column;
}

.group-publications-card .v-card__text {
  flex: 1 1 auto;
  max-height: 400px;
  /* ~3 ítems visibles antes de scroll */
  overflow-y: auto;
}

.group-publications-card .v-card__actions {
  flex-shrink: 0;
}
</style>
